<!-- Updated index page with COA preview and certificate icons -->
<template>
  <v-sheet class="mx-5">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <!-- Breadcrumbs -->
      <v-breadcrumbs>
        <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }">
          Home
        </v-breadcrumbs-item>
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item
          :exact="true"
          :to="{ name: 'inventoryLabResult', params: {} }"
          active
        >
          Inventory Lab Result
        </v-breadcrumbs-item>
      </v-breadcrumbs>

      <!-- Lots the lab has not tested or uploaded yet -->
      <lab-result-missing-lots :start-date="appliedStart" :end-date="appliedEnd" />

      <!-- Date Range Picker Section -->
      <div class="d-flex flex-column flex-lg-row mb-3">
        <!-- Start Date Picker -->
        <v-menu
          ref="startMenu"
          v-model="startMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
          z-index="100"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="startDate"
            @input="startMenu = false"
            :max="endDate"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="startMenu = false">
              Cancel
            </v-btn>
            <v-btn text color="primary" @click="$refs.startMenu.save(startDate)">
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>

        <!-- End Date Picker -->
        <v-menu
          ref="endMenu"
          v-model="endMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
          class="mx-2"
          z-index="100"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="endDate"
            @input="endMenu = false"
            :min="startDate"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="endMenu = false">
              Cancel
            </v-btn>
            <v-btn text color="primary" @click="$refs.endMenu.save(endDate)">
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </div>
      <!-- End of Date Range Picker Section -->

      <!-- Search and Action Buttons -->
      <div class="d-flex flex-column flex-lg-row">
        <v-text-field
          dense
          v-model="searchTerms"
          @keyup.enter="search"
          @click:clear="clearSearch"
          @click:append="search"
          placeholder="Press enter to begin search"
          append-icon="mdi-magnify"
          outlined
          clearable
          label="Search sample, document, program, quality, lot ID or lot document"
        />
        <div class="d-flex justify-space-around">
          <v-btn
            class="ml-0 ml-lg-4 mb-2 mb-lg-0 align-self-end align-self-lg-start"
            color="primary"
            :to="{ name: 'inventoryLabResultCreate', params: {} }"
          >
            New Lab Result
          </v-btn>
          <v-btn
            class="ml-0 ml-lg-4 mb-2 mb-lg-0 align-self-end align-self-lg-start"
            color="primary"
            :to="{ name: 'inventoryLabResultUpload', params: {} }"
          >
            Upload
          </v-btn>
        </div>
      </div>

      <div class="d-flex flex-column flex-lg-row">
        <!-- Submit Button -->
        <v-btn color="primary" @click="applyDateRange" class="mt-2 mt-md-0">
          Submit
        </v-btn>
      </div>
      <v-divider class="my-2" />

      <!-- Display Switches and Submit & Download Buttons -->
      <div class="d-flex justify-space-around flex-column flex-md-row align-items-center">
        <div class="d-flex align-items-center">
          <div class="font-weight-light mr-4 mt-1">Display</div>
          <v-switch
            @change="search"
            class="mt-0 mx-2"
            :value="true"
            label="Unassigned Only"
            v-model="unassignedOnly"
          />
          <!-- Hover explains what a flag means: people met the word with no idea. -->
          <v-tooltip top max-width="380" z-index="20">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-switch
                  @change="search"
                  class="mt-0 mx-2"
                  :value="true"
                  label="Flagged Only"
                  v-model="flaggedOnly"
                />
              </div>
            </template>
            <span>
              Flagged = the report sits on a lot that its own worksheet number or document
              disagrees with, e.g. a FINAL on a dross lot, or a lot with another document.
              Hover the orange ⚠ on a row to see why. Click it to keep that lot, or click the lot
              number to choose the right one; either way the flag goes.
            </span>
          </v-tooltip>
          <v-switch
            @change="search"
            class="mt-0 mx-2"
            :value="true"
            label="Final Test Only"
            v-model="finalTestOnly"
          />
        </div>
        
        <!-- Retry matching for reports uploaded before their inventory was entered -->
        <v-btn
          color="primary"
          outlined
          class="ml-4 mt-2 mt-md-0"
          @click="autoAssign"
          :disabled="isAutoAssigning"
        >
          <v-icon left>mdi-link-variant</v-icon>
          {{ isAutoAssigning ? `Auto-assigning ${autoAssignProgress}` : "Auto-assign Inventory" }}
        </v-btn>

        <!-- Download All Results Button with Loading State -->
        <v-btn
          color="secondary"
          class="ml-4 mt-2 mt-md-0"
          @click="downloadAll"
          :loading="isDownloading"
        >
          Download All Results
        </v-btn>
      </div>

      <!-- Data Table -->
      <div class="table-container">
        <div class="d-flex justify-end my-2"></div>
        <v-data-table
          id="lab-results-table"
          :headers="headers"
          :items="inventoryLabResultList"
          :options.sync="options"
          :items-per-page="pagination.pageSize"
          :loading="!inventoryLabResultProcessed"
          :server-items-length="pagination.totalSize"
          :page="pagination.current"
          item-key="id"
          class="elevation-1"
          :footer-props="{
            'items-per-page-options': [10, 20, 30, 40, 50, 100, 500]
          }"
          @update:options="pageChanged"
        >
          <template v-slot:item.inventoryId="{ item }">
            <div class="d-flex align-center" v-if="item.inventoryId">
              <a
                @click="
                  selectedRow = item;
                  lookupInventory = true;
                "
                >{{ item.inventoryId }}</a
              >
              <div class="certificate-icon-wrapper" @click="openPreview(item)">
                <v-badge
                  :content="'✔'"
                  :value="item.signed"
                  color="green"
                  overlap
                  bottom
                  right
                >
                  <v-icon class="ml-2">mdi-certificate</v-icon>
                </v-badge>
              </div>
              <!-- The lot disagrees with the report's worksheet number or document -->
              <v-tooltip v-if="item.lotCheck" right max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    class="ml-2"
                    color="orange darken-2"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmLot(item)"
                  >
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ item.lotCheck }}. Click to keep it on this lot.</span>
              </v-tooltip>
            </div>
            <a
              v-else
              style="white-space: nowrap"
              @click="
                lookupInventory = true;
                selectedRow = item;
              "
              >Assign</a
            >
          </template>
          <template v-slot:item.testTime="{ item }">
            <a style="white-space: nowrap" @click="view(item)">
              {{ item.testTime | formatDateTimeYear }}
            </a>
          </template>
          <template v-slot:item.finalTest="{ item }">
            <v-icon v-if="item.finalTest">mdi-check</v-icon>
            <v-icon v-else>mdi-close</v-icon>
          </template>
          <template v-slot:item.actions="{ item }">
            <div style="white-space: nowrap">
              <v-icon small class="mr-2" @click="edit(item)">
                mdi-pencil
              </v-icon>
              <v-icon small class="mr-2" @click="remove(item)">
                mdi-delete
              </v-icon>
            </div>
          </template>
        </v-data-table>

        <div class="my-5">
          <router-link :to="{ name: 'inventoryCoaCheck', params: {} }">
            Find inventory without CoA
          </router-link>
        </div>
      </div>
    </div>

    <!-- Confirmation and Lookup Components -->
    <confirmation-dialog ref="confirm" />
    <lab-result-lot-chooser
      :activated.sync="lookupInventory"
      :lab-result="selectedRow"
      @chosen="assignChosenLot"
      @unassign="leaveUnassigned"
    />
    <lab-result-review-dialog
      :activated.sync="reviewOpen"
      :start-date="startDate"
      :end-date="endDate"
      @changed="getResources"
    />

    <!-- COA Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="1200px" persistent>
      <coa-preview
        :lab-result="selectedLabResult"
        :key="selectedLabResult ? selectedLabResult.id : ''"
        @close="previewDialog = false"
        @signed="handleSigned"
      />
    </v-dialog>
  </v-sheet>
</template>

<script>
import { json2excel } from "js2excel";
import { mapActions, mapGetters } from "vuex";
import { stableSort } from "@/utils/fetchAllPages";
const LabResultLotChooser = () => import("./LabResultLotChooser.vue");
const LabResultReviewDialog = () => import("./LabResultReviewDialog.vue");
const LabResultMissingLots = () => import("./LabResultMissingLots.vue");
import { autoAssignWindows, addAutoAssignCounts, lastMonthsRange } from "./autoAssignWindows";
import { decideLot, alreadyReported } from "./labLotActions";
const CoaPreview = () => import("./CoAPreview.vue");

// The Excel export pages through its results. 200 is a page every profile will serve
// (production's ceiling is currently 600, the default is 200), and the cap below stops a
// runaway loop if the server ever reports a nonsense totalPages - 200 x 500 = 100,000
// rows, comfortably above the 20,366 lab results on production today.
const LAB_RESULT_EXPORT_PAGE_SIZE = 200;
const LAB_RESULT_EXPORT_PAGE_LIMIT = 500;

export default {
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 100,
      },
      inventoryLabResultList: [],
      allInventoryLabResultList: [], // For Download All
      showParentPage: true,
      lookupInventory: false,
      selectedRow: null,
      elements: [],
      options: {
        sortBy: ["testTime"],
        sortDesc: [true],
      },
      headers: [],
      baseHeaders: [
        {
          text: "Inventory ID",
          value: "inventoryId",
          align: "start",
          sortable: true,
        },
        {
          text: "Test Time",
          value: "testTime",
          align: "start",
          sortable: true,
        },
        {
          text: "Sample Name",
          value: "sampleName",
          align: "start",
          sortable: true,
        },
        {
          text: "Quality",
          value: "quality",
          align: "start",
          sortable: true,
        },
        {
          text: "AveroutN",
          value: "averoutN",
          align: "start",
          sortable: true,
        },
        {
          text: "Program",
          value: "program",
          align: "start",
          sortable: true,
        },
        {
          text: "Technician",
          value: "technician",
          align: "start",
          sortable: true,
        },
        {
          text: "Final",
          value: "finalTest",
          align: "start",
          sortable: true,
        },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
      unassignedOnly: false,
      flaggedOnly: false,
      finalTestOnly: false,

      // New Data Properties for Date Range
      startMenu: false,
      endMenu: false,
      startDate: null,
      endDate: null,

      // Loading State for Download All
      isDownloading: false,
      isAutoAssigning: false,
      autoAssignProgress: "",
      reviewOpen: false,
      // The range the list was last loaded for: the banner follows Submit, not every keystroke.
      appliedStart: null,
      appliedEnd: null,
      errorMsg: "Error downloading data",

      // Preview Dialog
      previewDialog: false,
      selectedLabResult: null,
    };
  },
  components: {
    LabResultLotChooser,
    LabResultReviewDialog,
    LabResultMissingLots,
    CoaPreview,
  },
  filters: {
    negativeNumber: function (value) {
      if (value && value > 0) {
        return value;
      } else if (value < 0) {
        return `<${Math.abs(value)}`;
      } else {
        return 0;
      }
    },
    formatDateTimeYear(value) {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleString("default", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  computed: {
    ...mapGetters("inventoryLabResult", [
      "inventoryLabResultProcessed",
      "getInventoryLabResults",
    ]),
    canDownloadAll() {
      // Enable Download All button only if there are results to download
      return this.allInventoryLabResultList.length > 0;
    },
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addInfoMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("inventoryLabResult", [
      "deleteInventoryLabResult",
      "fetchInventoryLabResults",
      "updateInventoryLabResult",
      "autoAssignInventoryLabResults",
    ]),
    view(item) {
      this.$router.push({
        name: "inventoryLabResultView",
        params: { id: item.id },
      });
    },
    edit(item) {
      this.$router.push({
        name: "inventoryLabResultEdit",
        params: { id: item.id },
      });
    },
    async remove(item) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this inventory lab result?"
      );
      if (confirm) {
        this.deleteInventoryLabResult(item.id)
          .then(() => {
            this.addSuccessMessage("Inventory lab result deleted");
            this.getResources();
          })
          .catch(() => {
            this.addErrorMessage("Server responded with an error.");
          });
      }
    },
    getResources() {
      this.appliedStart = this.startDate;
      this.appliedEnd = this.endDate;
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "createTime";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;

      // Prepare date parameters as YYYY-MM-DD without time
      const startDate = this.startDate ? this.startDate : null;
      const endDate = this.endDate ? this.endDate : null;

      const params = {
        page:
          this.pagination.current && this.pagination.current > 0
            ? this.pagination.current - 1
            : 0,
        size: this.pagination.pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        searchTerm: this.searchTerms,
        unassignedOnly: this.unassignedOnly,
        flaggedOnly: this.flaggedOnly,
        finalTest: this.finalTestOnly ? true : null,
        startTime: startDate, // Send as 'YYYY-MM-DD'
        endTime: endDate,     // Send as 'YYYY-MM-DD'
      };

      this.fetchInventoryLabResults(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            if (this.pagination.totalSize > 0) {
              this.inventoryLabResultList = response.content;
              // A column for every element on the page, not just the first row's -
              // an XRF slag result carries elements a spark result does not.
              const pageElements = new Map();
              this.inventoryLabResultList.forEach((item) => {
                item.elements.forEach((element) => {
                  if (!pageElements.has(element.element.symbol)) {
                    pageElements.set(element.element.symbol, element);
                  }
                });
              });
              this.prepareElementHeaders([...pageElements.values()]);

              this.inventoryLabResultList.forEach((item) => {
                item.elements.forEach((element) => {
                  item[element.element.symbol] = element.compositionPercent;
                });
              });
            } else {
              this.inventoryLabResultList = [];
            }
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server responded with an error: " + error);
        });
    },
    showParent(show) {
      this.showParentPage = show;
    },
    pageChanged(pagination) {
      this.pagination.current = pagination.page;
      this.pagination.pageSize = pagination.itemsPerPage;

      this.getResources();
    },
    // A person decided no lot is the report's: take it off any lot and confirm that, so
    // auto-assign leaves it unassigned.
    async leaveUnassigned() {
      await this.decide(this.selectedRow, null, (row) => `${row.sampleName} left unassigned`);
    },
    // A person chose the report's lot: put it there and confirm it, so it is not flagged.
    async assignChosenLot(inventoryId) {
      if (!inventoryId) return;
      await this.decide(this.selectedRow, inventoryId, (row) => `Lot ${inventoryId} confirmed for ${row.sampleName}`);
    },
    async decide(row, inventoryId, success) {
      if (!row) return;
      try {
        await decideLot(this.$axios, row.id, inventoryId);
        this.addSuccessMessage(success(row));
      } catch (error) {
        if (!alreadyReported(error)) {
          this.addErrorMessage("Could not save the lot.");
        }
      } finally {
        // Whatever reached the server, show it.
        this.getResources();
      }
    },
    // Re-run the upload's inventory matching over unassigned results in the
    // selected date range - for reports uploaded before the process was entered.
    async autoAssign() {
      if (!this.startDate || !this.endDate) {
        this.addErrorMessage("Both Start Date and End Date must be selected.");
        return;
      }
      const windows = autoAssignWindows(this.startDate, this.endDate);
      if (windows.length === 0) {
        this.addErrorMessage("Start Date must be before End Date.");
        return;
      }
      this.isAutoAssigning = true;
      let result = addAutoAssignCounts({}, null);
      let done = 0;
      try {
        // A month at a time: a long range in one call outruns the 60-second request limit.
        for (const window of windows) {
          this.autoAssignProgress = `${done + 1} / ${windows.length}`;
          result = addAutoAssignCounts(result, await this.autoAssignInventoryLabResults(window));
          done++;
        }
        let message = `Assigned ${result.assigned} of ${result.checked} unassigned lab result(s).`;
        if (result.ambiguous > 0) {
          message += ` ${result.ambiguous} match several inventories - please assign them by hand.`;
        }
        // Reports an earlier upload matched by their XRF Reading No, not their worksheet number.
        if (result.relinked > 0) {
          message += ` Moved ${result.relinked} to the inventory their worksheet produced.`;
        }
        if (result.unlinked > 0) {
          message += ` Unassigned ${result.unlinked} from a wrong inventory - they wait for their worksheet's output.`;
        }
        if (result.flagged > 0) {
          message += ` ${result.flagged} sit on a lot their worksheet number or document disagrees with - please confirm them.`;
        }
        if (result.assigned > 0 || result.relinked > 0 || result.unlinked > 0) {
          this.addSuccessMessage(message);
        } else {
          this.addInfoMessage(message);
        }
        this.getResources();
        // The mismatched ones are not decided by the rules: ask the person.
        if (result.flagged > 0) {
          this.reviewOpen = true;
        }
      } catch (error) {
        // The axios interceptor already reports network errors, 401/403s and 500s (with
        // the server's reason) - never overwrite those; report everything else, including
        // errors raised here in the browser.
        const status = error && error.status;
        const reported =
          (error && error.isAxiosError) || [401, 403, 500].includes(status);
        if (!reported) {
          const reason =
            (error && error.data && error.data.message) || (error && error.message);
          this.addErrorMessage("Auto-assign failed" + (reason ? ": " + reason : "."));
        }
        if (done > 0) {
          // What the finished months changed is saved; say where it stopped.
          this.addInfoMessage(
            `Auto-assign finished ${windows[0].startTime} to ${windows[done - 1].endTime} before it stopped; run it again from ${windows[done].startTime}.`
          );
          this.getResources();
        }
      } finally {
        this.isAutoAssigning = false;
        this.autoAssignProgress = "";
      }
    },
    search() {
      this.getResources();
    },
    clearSearch() {
      this.searchTerms = "";
      this.getResources();
    },
    prepareElementHeaders(elements) {
      this.elements = [];
      const headers = [...this.baseHeaders];

      if (elements && elements.length > 0) {
        elements.forEach((element) => {
          headers.push({
            text: element.element.symbol,
            value: element.element.symbol,
            align: "start",
            sortable: false,
          });
        });

        this.elements = elements.map((e) => e.element.symbol);
        headers.push({
          text: "Actions",
          value: "actions",
          align: "end",
          sortable: false,
        });
      }

      this.headers = headers;
    },
    // Opens on the last three months up to today. A calendar quarter opened nearly empty on
    // the first days of a new quarter, so last month's reports needed the dates changed.
    setDefaultDates() {
      const { startDate, endDate } = lastMonthsRange(new Date(), 3);
      this.startDate = startDate;
      this.endDate = endDate;
    },
    // Method to Apply Date Range
    applyDateRange() {
      if (this.startDate && this.endDate) {
        // Validate date range
        if (new Date(this.startDate) > new Date(this.endDate)) {
          this.addErrorMessage("Start Date must be before End Date.");
          return;
        }
        this.getResources();
      } else {
        this.addErrorMessage("Both Start Date and End Date must be selected.");
      }
    },
    // Method to Download All Results with Loading State
    async downloadAll() {
      this.isDownloading = true; // Start loading

      try {
        const sortBy =
          this.options["sortBy"] && this.options["sortBy"].length > 0
            ? this.options.sortBy[0]
            : "createTime";
        const sortDesc =
          this.options["sortDesc"] && this.options["sortDesc"].length > 0
            ? this.options.sortDesc[0]
            : true;

        // Prepare date parameters as YYYY-MM-DD without time
        const startDate = this.startDate ? this.startDate : null;
        const endDate = this.endDate ? this.endDate : null;

        // This is the Excel export, so it really does want every matching row - but
        // asking for size=1000000 does not deliver them. The server caps a page at
        // max-page-size and returns the first page silently, so the user was handed a
        // TRUNCATED spreadsheet that looked complete. A download that quietly loses
        // rows is worse than a slow one: page through instead of guessing a number.
        const baseParams = {
          size: LAB_RESULT_EXPORT_PAGE_SIZE,
          // `id` as a tiebreaker: paging through a changing table on a non-unique sort
          // key (testTime is not unique) can show a row twice or skip one, because rows
          // sharing a timestamp have no defined order between queries.
          sort: stableSort(`${sortBy}${sortDesc ? ",desc" : ""}`),
          searchTerm: this.searchTerms,
          unassignedOnly: this.unassignedOnly,
          flaggedOnly: this.flaggedOnly,
          finalTest: this.finalTestOnly ? true : null,
          startTime: startDate, // Send as 'YYYY-MM-DD'
          endTime: endDate,     // Send as 'YYYY-MM-DD'
        };

        const rows = [];
        let page = 0;
        let totalPages = 1;
        do {
          const response = await this.fetchInventoryLabResults({
            ...baseParams,
            page,
          });
          if (!response) {
            break;
          }
          rows.push(...(response.content || []));
          // totalPages reflects the size the SERVER used, not the one we asked for,
          // so a clamp cannot make this loop stop early.
          totalPages = response.totalPages || 1;
          page += 1;
        } while (page < totalPages && page < LAB_RESULT_EXPORT_PAGE_LIMIT);

        if (rows.length) {
          const response = { content: rows };
          const allInventoryLabResultList = response.content.map((item) => {
            // Flatten elements for download if needed
            let flatItem = { ...item };
            if (item.elements && item.elements.length > 0) {
              item.elements.forEach((element) => {
                flatItem[element.element.symbol] = element.compositionPercent;
              });
              delete flatItem.elements;
            }
            return flatItem;
          });

          // Trigger download
          this.downloadExcel(allInventoryLabResultList);
        }
      } catch (error) {
        this.addErrorMessage("Failed to download all results: " + error);
      } finally {
        this.isDownloading = false; // End loading
      }
    },
    // Method to Handle Excel Download
    downloadExcel(data) {
      if (data && data.length > 0) {
        try {
          json2excel({
            data,
            name: 'Inventory-Lab-Results-All',
            formateDate: "yyyy-mm-dd",
          });
          this.addSuccessMessage("Download initiated successfully.");
        } catch (e) {
          this.addErrorMessage(this.errorMsg);
        }
      } else {
        this.addErrorMessage("No data found to download");
      }
    },
    openPreview(item) {
      if (!item.inventoryId) return;
      this.selectedLabResult = item;
      this.previewDialog = true;
    },
    handleSigned() {
      this.getResources();
    },
    // A person looked at a flagged report and keeps it on its lot: the flag goes away.
    async confirmLot(item) {
      const keep = await this.$refs.confirm.open(
        "Keep this lot?",
        `${item.lotCheck}. Keep this report on lot ${item.inventoryId}? To move it instead, click the lot and pick another.`
      );
      if (!keep) return;
      try {
        await this.$axios.post(`/inventory-lab-result/${item.id}/confirm-lot`);
        this.addSuccessMessage("Lot confirmed - the flag is cleared.");
        this.getResources();
      } catch (error) {
        const reported =
          (error && error.isAxiosError) || [401, 403, 500].includes(error && error.status);
        if (!reported) {
          this.addErrorMessage("Could not confirm the lot.");
        }
      }
    },
  },
  watch: {
    "pagination.numberRegisterForPage": function () {
      this.pagination.current = -1;
    },
    // Watch for changes in allInventoryLabResultList to trigger download
    allInventoryLabResultList(newVal) {
      if (newVal.length > 0) {
        // The Download component will reactively download when allInventoryLabResultList is populated
        // No additional action needed if Download component watches 'value' prop
        // The download is already triggered via ref in downloadAll method
      }
    },
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
  created() {
    this.setDefaultDates();
    this.getResources();
  },
};
</script>

<style>
#lab-results-table thead th,
#lab-results-table tbody td {
  white-space: nowrap;
}

#lab-results-table thead th:first-child {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  left: 0;
  z-index: 9;
  opacity: 0.95;
  background-color: white;
  overflow: hidden;
  text-overflow: ellipsis;
}

#lab-results-table tbody td:first-child {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  left: 0;
  z-index: 9;
  opacity: 0.95;
  background-color: white;
  overflow: hidden;
  text-overflow: ellipsis;
}

.certificate-icon-wrapper {
  cursor: pointer;
}
</style>